import React, { useState, useEffect, useMemo } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  TextField,
  InputAdornment,
  FormHelperText,
  CircularProgress,
  Alert,
  IconButton,
  Typography,
  ListSubheader,
  ListItemText,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  LocationOn as LocationOnIcon,
  Clear as ClearIcon,
  LocationCity as LocationCityIcon,
} from '@mui/icons-material';

import { contractService, type BairroInstalacaoOption } from '../../services/contractService';

interface BairroSelectProps {
  value?: string | null;
  onChange: (value: string | null) => void;
  label?: string;
  size?: 'small' | 'medium';
  error?: boolean;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  allowClear?: boolean;
  /** Permite digitar um bairro que não esteja na lista (modo formulário). */
  allowCustom?: boolean;
  fullWidth?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
  escritorio_id?: number;
  cidade_inst?: string;
  estado_inst?: string;
}

const BairroSelect: React.FC<BairroSelectProps> = ({
  value,
  onChange,
  label = 'Bairro',
  size = 'small',
  error = false,
  helperText,
  required = false,
  disabled = false,
  placeholder = 'Selecione um bairro',
  allowClear = true,
  allowCustom = false,
  fullWidth = true,
  variant = 'outlined',
  escritorio_id,
  cidade_inst,
  estado_inst,
}) => {
  const [allBairros, setAllBairros] = useState<BairroInstalacaoOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadBairros = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const data = await contractService.getBairrosInstalacao({
        search: searchTerm || undefined,
        escritorio_id,
        cidade: cidade_inst,
        estado: estado_inst,
      });
      setAllBairros(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar bairros';
      console.error('Erro ao carregar bairros:', err);
      setLoadError(message);
      setAllBairros([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadBairros();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, escritorio_id, cidade_inst, estado_inst]);

  const bairrosExibicao = useMemo(() => {
    if (!value?.trim()) return allBairros;
    if (allBairros.some((b) => b.bairro === value)) return allBairros;
    return [{ bairro: value, quantidade: 0, cidade_inst: '', estado_inst: '' }, ...allBairros];
  }, [allBairros, value]);

  const bairrosPorCidade = useMemo(() => {
    const map = new Map<string, { titulo: string; bairros: BairroInstalacaoOption[] }>();

    bairrosExibicao.forEach((item) => {
      const cidade = item.cidade_inst?.trim() || 'Sem cidade';
      const estado = item.estado_inst?.trim() || '';
      const chave = `${estado}|${cidade}`;
      const titulo = estado ? `${cidade} / ${estado}` : cidade;

      if (!map.has(chave)) {
        map.set(chave, { titulo, bairros: [] });
      }
      map.get(chave)!.bairros.push(item);
    });

    return Array.from(map.entries())
      .map(([chave, data]) => ({
        chave,
        titulo: data.titulo,
        bairros: data.bairros.sort((a, b) => a.bairro.localeCompare(b.bairro, 'pt-BR')),
      }))
      .sort((a, b) => a.titulo.localeCompare(b.titulo, 'pt-BR'));
  }, [bairrosExibicao]);

  const handleChange = (event: { target: { value: unknown } }) => {
    const selected = event.target.value;
    onChange(selected === '' || selected == null ? null : String(selected));
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange(null);
  };

  const renderValue = (selected: unknown) => {
    if (!selected || selected === '') {
      return <Box component="span" sx={{ color: 'text.disabled' }}>{placeholder}</Box>;
    }
    const bairro = bairrosExibicao.find((b) => b.bairro === selected);
    if (!bairro) return String(selected);
    const local = [bairro.cidade_inst, bairro.estado_inst].filter(Boolean).join(' / ');
    return local ? `${bairro.bairro} — ${local}` : bairro.bairro;
  };

  const renderBairroContent = (item: BairroInstalacaoOption) => (
    <Box display="flex" alignItems="center" gap={0.75} width="100%" minWidth={0}>
      <LocationOnIcon sx={{ fontSize: 16 }} color="action" />
      <ListItemText
        primary={
          <Typography variant="body2" fontWeight="bold" noWrap sx={{ fontSize: '0.8125rem', lineHeight: 1.3 }}>
            {item.bairro}
          </Typography>
        }
        secondary={
          item.cidade_inst || item.estado_inst
            ? `${item.cidade_inst || ''}${item.cidade_inst && item.estado_inst ? ' / ' : ''}${item.estado_inst || ''}`
            : undefined
        }
        sx={{ my: 0 }}
        primaryTypographyProps={{ component: 'div' }}
        secondaryTypographyProps={{ component: 'div', fontSize: '0.7rem', lineHeight: 1.2 }}
      />
    </Box>
  );

  const renderBairroMenuItem = (item: BairroInstalacaoOption) => (
    <MenuItem key={item.bairro} value={item.bairro} dense sx={{ py: 0.5, minHeight: 36, pl: 2 }}>
      {renderBairroContent(item)}
    </MenuItem>
  );

  const getOptionLabel = (option: string | BairroInstalacaoOption) =>
    typeof option === 'string' ? option : option.bairro;

  const groupByCidade = (option: BairroInstalacaoOption) => {
    const cidade = option.cidade_inst?.trim() || 'Sem cidade';
    const estado = option.estado_inst?.trim() || '';
    return estado ? `${cidade} / ${estado}` : cidade;
  };

  const renderAutocompleteOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: BairroInstalacaoOption
  ) => (
    <Box component="li" {...props} key={option.bairro} sx={{ py: 0.5, minHeight: 36 }}>
      {renderBairroContent(option)}
    </Box>
  );

  if (allowCustom) {
    return (
      <Autocomplete
        freeSolo
        fullWidth={fullWidth}
        size={size}
        disabled={disabled}
        options={bairrosExibicao}
        getOptionLabel={getOptionLabel}
        groupBy={groupByCidade}
        value={value ?? ''}
        onChange={(_, newValue) => {
          if (newValue == null) {
            onChange(null);
            return;
          }
          const selected = typeof newValue === 'string' ? newValue : newValue.bairro;
          onChange(selected?.trim() ? selected : null);
        }}
        onInputChange={(_, newInput, reason) => {
          if (reason === 'input' || reason === 'clear') {
            setSearchTerm(newInput);
            onChange(newInput.trim() ? newInput : null);
          }
        }}
        isOptionEqualToValue={(option, val) => getOptionLabel(option) === getOptionLabel(val as string | BairroInstalacaoOption)}
        renderOption={renderAutocompleteOption}
        noOptionsText={
          loadError
            ? loadError
            : loading
              ? 'Carregando bairros...'
              : 'Nenhum bairro cadastrado — digite o nome'
        }
        loading={loading}
        ListboxProps={{
          sx: {
            maxHeight: 320,
            py: 0.5,
            '& .MuiAutocomplete-groupLabel': { lineHeight: '28px', py: 0 },
            '& .MuiAutocomplete-option': { minHeight: 36, py: 0.5 },
          },
        }}
        componentsProps={{ paper: { sx: { mt: 0.5 } } }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            error={error}
            //helperText={helperText || 'Selecione um bairro existente ou digite o nome'}
            placeholder={placeholder}
            variant={variant}
            FormHelperTextProps={{ sx: { mt: 0.25, mx: 0, lineHeight: 1.3 } }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start" sx={{ mr: 0 }}>
                    <LocationOnIcon sx={{ fontSize: 18 }} />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    );
  }

  return (
    <FormControl
      fullWidth={fullWidth}
      size={size}
      error={error}
      variant={variant}
      disabled={disabled}
    >
      <InputLabel required={required}>{label}</InputLabel>

      <Select
        value={value ?? ''}
        label={label}
        onChange={handleChange}
        displayEmpty={allowClear}
        renderValue={renderValue}
        MenuProps={{
          disableScrollLock: true,
          PaperProps: { style: { maxHeight: 360 } },
          TransitionProps: {
            onExited: () => setSearchTerm(''),
          },
        }}
        startAdornment={
          <InputAdornment position="start" sx={{ ml: 0.5 }}>
            <LocationOnIcon sx={{ fontSize: 18 }} />
          </InputAdornment>
        }
        endAdornment={
          allowClear && value ? (
            <InputAdornment position="end" sx={{ mr: 2 }}>
              <ClearIcon
                onClick={handleClear}
                sx={{ cursor: 'pointer', fontSize: 18, '&:hover': { color: 'error.main' } }}
              />
            </InputAdornment>
          ) : undefined
        }
      >
        <Box sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar bairro..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')} sx={{ p: 0.25 }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.50',
              },
            }}
          />
          {escritorio_id && (
            <Box sx={{ mt: 0.5, px: 0.5 }}>
              <Typography variant="caption" color="primary.main" sx={{ lineHeight: 1.2 }}>
                Bairros dos contratos do escritório selecionado
              </Typography>
            </Box>
          )}
        </Box>

        {loading && bairrosExibicao.length === 0 && (
          <MenuItem disabled dense sx={{ minHeight: 32, py: 0.5 }}>
            <Box display="flex" alignItems="center" gap={0.75}>
              <CircularProgress size={14} />
              <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>Carregando bairros...</Typography>
            </Box>
          </MenuItem>
        )}

        {loadError && (
          <MenuItem disabled dense sx={{ py: 0.5 }}>
            <Alert severity="error" sx={{ width: '100%', py: 0.25 }}>
              {loadError}
            </Alert>
          </MenuItem>
        )}

        {allowClear && !required && !loadError && (
          <MenuItem value="" dense sx={{ minHeight: 32, py: 0.5 }}>
            <em>Todos os bairros</em>
          </MenuItem>
        )}

        {!loadError &&
          bairrosPorCidade.flatMap((grupo) => [
            <ListSubheader
              key={`cidade-${grupo.chave}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                lineHeight: '30px',
                py: 0,
                bgcolor: 'action.hover',
              }}
            >
              <LocationCityIcon sx={{ fontSize: 16 }} color="primary" />
              <Typography variant="body2" fontWeight={700} component="span" sx={{ fontSize: '0.75rem' }}>
                {grupo.titulo}
              </Typography>
            </ListSubheader>,
            ...grupo.bairros.map((item) => renderBairroMenuItem(item)),
          ])}

        {!loading && !loadError && bairrosExibicao.length === 0 && (
          <MenuItem disabled dense sx={{ minHeight: 32, py: 0.5 }}>
            <em>Nenhum bairro encontrado nos contratos</em>
          </MenuItem>
        )}
      </Select>

      {helperText && <FormHelperText sx={{ mt: 0.25, mx: 0 }}>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default BairroSelect;
